type ColorShade = {
  DEFAULT: string;
  hover: string;
  active: string;
  bg: string;
  text: string;
};

type ColorVariant = {
  light: ColorShade;
  dark: ColorShade;
};

type ColorScheme = {
  primary: ColorVariant;
  secondary: ColorVariant;
  success: ColorVariant;
  danger: ColorVariant;
  warning: ColorVariant;
  info: ColorVariant;
};

export const colors: ColorScheme = {
  primary: {
    light: {
      DEFAULT: '#ffd62e',
      hover: '#e6be19',
      active: '#cca605',
      bg: '#fffbeb',
      text: '#947000',
    },
    dark: {
      DEFAULT: '#ffd62e',
      hover: '#ffe066',
      active: '#ffeb99',
      bg: '#3b3206',
      text: '#ffd62e',
    }
  },
  secondary: {
    light: {
      DEFAULT: '#035fa3',
      hover: '#024d85',
      active: '#023c67',
      bg: '#eff6fc',
      text: '#035fa3',
    },
    dark: {
      DEFAULT: '#2888cf',
      hover: '#4ea0db',
      active: '#79b8e6',
      bg: '#06243d',
      text: '#cde6fa',
    }
  },
  success: {
    light: {
      DEFAULT: '#16A34A', // green-600
      hover: '#15803D',   // green-700
      active: '#166534',  // green-800
      bg: '#F0FDF4',      // green-50
      text: '#16A34A',    // green-600
    },
    dark: {
      DEFAULT: '#22C55E', // green-500
      hover: '#4ADE80',   // green-400
      active: '#86EFAC',  // green-300
      bg: '#166534',      // green-900
      text: '#DCFCE7',    // green-100
    }
  },
  danger: {
    light: {
      DEFAULT: '#DC2626', // red-600
      hover: '#B91C1C',   // red-700
      active: '#991B1B',  // red-800
      bg: '#FEF2F2',      // red-50
      text: '#DC2626',    // red-600
    },
    dark: {
      DEFAULT: '#EF4444', // red-500
      hover: '#F87171',   // red-400
      active: '#FCA5A5',  // red-300
      bg: '#7F1D1D',      // red-900
      text: '#FEE2E2',    // red-100
    }
  },
  warning: {
    light: {
      DEFAULT: '#f59e0b',
      hover: '#d97706',
      active: '#b45309',
      bg: '#fffbeb',
      text: '#92400e',
    },
    dark: {
      DEFAULT: '#fbbf24',
      hover: '#f59e0b',
      active: '#d97706',
      bg: '#451a03',
      text: '#fef3c7',
    }
  },
  info: {
    light: {
      DEFAULT: '#0284c7',
      hover: '#0369a1',
      active: '#075985',
      bg: '#f0f9ff',
      text: '#0369a1',
    },
    dark: {
      DEFAULT: '#38bdf8',
      hover: '#0ea5e9',
      active: '#0284c7',
      bg: '#082f49',
      text: '#e0f2fe',
    }
  }
};

// Helper function to convert hex to RGB values
export function hexToRgb(hex: string): [number, number, number] {
  // Remove the hash if it exists
  const cleanHex = hex.replace('#', '');
  
  // Parse the hex values
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  
  return [r, g, b];
}

// Helper function to convert RGB values to CSS variable format
export function rgbToCssVar(r: number, g: number, b: number): string {
  return `${r} ${g} ${b}`;
}

// Generate CSS variables for the color scheme
export function generateColorVariables(): Record<string, string> {
  const variables: Record<string, string> = {};
  
  Object.entries(colors).forEach(([colorName, variants]) => {
    Object.entries(variants).forEach(([variant, shades]) => {
      Object.entries(shades).forEach(([shade, value]) => {
        const [r, g, b] = hexToRgb(value);
        variables[`--color-${colorName}-${variant}-${shade}`] = rgbToCssVar(r, g, b);
      });
    });
  });

  return variables;
}